import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const workplaceSystems = [
  "Fire detectors and alarms",
  "Wet fire suppression systems",
  "Gas detectors",
  "Engineered smoke control systems",
  "Fire protection systems",
  "Gas suppression systems",
];

const workplaceProducts = [
  "Exit and emergency lights",
  "Fire hydrants",
  "Portable fire extinguishers",
  "Fire sprinklers",
  "Heat and smoke detectors",
  "Fire rated doors",
  "Fire pumps",
  "Fire blankets",
  "Fire hose reel",
  "Smoke escape masks",
];

const precautions = [
  { icon: "ri-forbid-line", text: "Avoid tampering with fire safety systems" },
  { icon: "ri-flask-line", text: "Store and use chemicals and other inflammable materials safely" },
  { icon: "ri-plug-line", text: "Observe electrical safety at all times" },
  { icon: "ri-search-line", text: "Regular inspection, maintenance and servicing of fire protection systems" },
];

const assessmentSteps = [
  { step: "01", title: "Identify Fire Hazards", desc: "Identifying fire hazards both in and out of the premises, including potential ignition sources, fuel sources, and oxygen suppliers." },
  { step: "02", title: "Identify People at Risk", desc: "Identifying the people at risk including workers, contractors, visitors, and others who may be present in the building." },
  { step: "03", title: "Evaluate, Remove & Reduce Threats", desc: "Evaluating identified hazards, removing them where possible, and implementing measures to reduce the remaining risks." },
  { step: "04", title: "Record, Plan & Train", desc: "Recording the findings, preparing an emergency plan and training employees on how to execute the plan effectively." },
];

export default function SafetyAtWorkPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "Fire Safety at Work Singapore",
            description:
              "Comprehensive guide to fire safety at work in Singapore, including fire risk assessments, installing fire systems, and fire safety training for commercial and industrial buildings.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/246a9e4e-8a52-4402-aee2-cc6708e2467c_compressed_dreamstime_m_121976517.webp"
              alt="Health and safety at work note"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">Safety at Work</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Workplace Fire Safety Guide
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Fire <span className="text-primary-400">Safety at Work</span> Singapore
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Commercial and industrial buildings are the most common areas where fire emergencies are experienced in Singapore. Total fire safety and protection requires you to understand the concepts of safety at work.
            </p>
          </div>
        </section>

        {/* Intro */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Why It Matters
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Why Fire Safety at Work is Critical
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-4">
                Fire incidences also occur in homes but commercial and industrial buildings are the most common areas where fire emergencies are experienced in Singapore. Today, most buildings have a wide range of fire safety and protection systems aimed at saving lives and minimizing the risks of property damage during fires.
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                Without proper measures for safety at work, your workers and assets could be in great danger whenever a fire incident occurs. There are a number of requirements that owners of commercial and industrial premises should meet in order to ensure the safety of their employees and assets during fire emergencies.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Get a Workplace Fire Assessment
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
              <img
                src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/573f921a-65b2-4ae7-8359-862864f9192b_compressed_de149eee-560c-41e7-b1af-a35c209a6f13.webp"
                alt="Ezzogenics workers installing emergency lighting on site"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Fire Risk Assessment */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                Step 1
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                Regular Fire <span className="text-primary-500">Risk Assessments</span>
              </h2>
              <p className="text-foreground-600 text-base max-w-2xl mx-auto">
                The risks of fire in workplaces usually vary from one to another. International fire safety codes require consistent fire risk assessments for all buildings occupied by more than five people.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {assessmentSteps.map((item) => (
                <div key={item.step} className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mb-4">
                    <span className="text-background-50 font-heading font-bold text-sm">{item.step}</span>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-base mb-2">{item.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-accent-50 border border-accent-200/70 rounded-2xl p-5 flex items-start gap-4 max-w-3xl mx-auto">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                <i className="ri-information-line text-accent-600 text-lg"></i>
              </div>
              <p className="text-foreground-700 text-sm leading-relaxed">
                Since fire threats may change, it is advisable that fire risk assessments are regularly reviewed and updated. For effective fire safety risk assessments on your building, simply hire professional fire safety testing and protection services.
              </p>
            </div>
          </div>
        </section>

        {/* Installing Systems & Products */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Step 2
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Installing Fire Systems <span className="text-primary-500">&amp; Solutions</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-2xl mx-auto">
              Depending on the results of the regular fire risk assessments and your fire safety needs, there are various kinds of fire systems and solutions that should be in the premises.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden mb-10 h-56 md:h-72">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/559f0c59-2822-47a9-a0ea-d740be9e162d_compressed_858db20a-d596-4d88-8cd3-61656154c13b.webp"
              alt="Ceiling fire safety detection and lighting systems installed in Singapore office"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Essential Fire Systems</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {workplaceSystems.map((system) => (
                  <div key={system} className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3 border border-background-200/70">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-check-line text-primary-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-700 text-sm">{system}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Recommended Fire Safety Products</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {workplaceProducts.map((product) => (
                  <div key={product} className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3 border border-background-200/70">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-shield-check-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-700 text-sm">{product}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Fire Safety Training */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="rounded-2xl overflow-hidden h-72 md:h-96">
                <img
                  src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/2de460d8-8bec-47ae-bf51-ef88d2e52bf4_compressed_752755b0-8448-47d7-a0e4-7eafc6a27b43.webp"
                  alt="Completed emergency lighting installation at bus interchange Singapore"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                  Step 3
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  Fire Safety <span className="text-primary-500">Training</span>
                </h2>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  The employees are the heart of every business and, also the ones with the greatest risk of injuries and accidents during fire incidences on commercial and industrial spaces. Since they are likely to be in the building during fires, it is critical that they are properly trained on fire protection and safety in the workplace.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-4">
                  Fire safety training for workers will ensure that they fully understand how to safely and effectively respond to emergencies, thereby significantly reducing the risks of accidents and destruction of assets.
                </p>
                <p className="text-foreground-600 text-base leading-relaxed mb-6">
                  Fire safety training should be offered to both old and new employees and reviewed regularly. The training should focus on implementing the results of fire risk assessment as well as fire safety codes. The management can also initiate occasional fire emergency drills to evaluate the effectiveness of the training.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-primary-500 font-semibold text-sm hover:text-primary-600 transition-colors underline underline-offset-4"
                >
                  Enquire about fire safety training
                  <i className="ri-arrow-right-line text-base"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Common Precautions */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Best Practices
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Common Fire Safety <span className="text-primary-500">Precautions at Work</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto">
              There are general fire safety precautions at the workplace that workers should observe to protect themselves and the assets therein.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 max-w-5xl mx-auto">
            {precautions.map((item) => (
              <div key={item.text} className="bg-background-100 rounded-2xl p-6 border border-background-200/70 text-center flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${item.icon} text-primary-600 text-xl`}></i>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-100 border-t border-background-200/70 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Get Ultimate Fire Safety Solutions for Your Workplace
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
              At Ezzogenics, we offer the ultimate fire safety solutions including fire protection systems, fire safety products, consultancy services as well as maintenance and repair of fire protection systems.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Get a Free Assessment
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                Browse Fire Safety Products
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}